import React from 'react';
import { Link } from 'react-router-dom';
import {
  GuideCode,
  GuideCta,
  GuideCtaActions,
  GuideLayout,
  GuideList,
  GuideParagraph,
  GuideSection,
} from '@/components/marketing/GuideLayout';
import { CLI_DOCS_URL } from '@/lib/contact';
import { PUBLIC_PAGES } from '@/lib/seo/public-routes';

const CiCdQualityGates: React.FC = () => (
  <GuideLayout
    route={PUBLIC_PAGES.ciCdQualityGates}
    eyebrow="Implementation guide"
    title="CI/CD quality gates that teams keep"
    intro="Adding a quality gate to a pipeline takes an afternoon. Choosing a threshold the team will still respect in three months is the hard part, and it is where most gates quietly die."
  >
    <GuideSection title="Why most quality gates get switched off">
      <GuideParagraph>
        The usual sequence: someone enables a gate at a strict threshold, the next morning every
        open pull request is red, and by the end of the week the gate is either disabled or routinely
        overridden. Nothing about the code changed. The gate simply asserted a standard the codebase
        had never met.
      </GuideParagraph>
      <GuideParagraph>
        A gate is a promise about what you will not merge. If it fires on work nobody intends to fix
        today, the team learns to ignore it, and you have added friction without adding signal.
      </GuideParagraph>
    </GuideSection>

    <GuideSection title="Pick the threshold from your current state">
      <GuideParagraph>
        DebtDrone gates on severity. <code className="mono rounded bg-surface-2 px-1.5 py-0.5 text-[13px]">--fail-on</code>{' '}
        takes <code className="mono rounded bg-surface-2 px-1.5 py-0.5 text-[13px]">critical</code>,{' '}
        <code className="mono rounded bg-surface-2 px-1.5 py-0.5 text-[13px]">high</code>,{' '}
        <code className="mono rounded bg-surface-2 px-1.5 py-0.5 text-[13px]">medium</code>,{' '}
        <code className="mono rounded bg-surface-2 px-1.5 py-0.5 text-[13px]">low</code>, or{' '}
        <code className="mono rounded bg-surface-2 px-1.5 py-0.5 text-[13px]">none</code>, and fails
        the command when it finds an issue at or above that level.
      </GuideParagraph>
      <GuideList
        items={[
          {
            term: 'Start by scanning without a gate',
            detail:
              'run the scan in CI with --fail-on none for a week and look at what it reports. You are measuring your baseline, not enforcing anything yet.',
          },
          {
            term: 'Set the gate just above your baseline',
            detail:
              'if the repository already carries high-severity findings, gating on high fails every build on day one. Gate on critical, and let high be reported.',
          },
          {
            term: 'Tighten on a schedule, not on a whim',
            detail:
              'move the threshold down one step only once the level above it is reliably clean. A gate that ratchets is a gate people trust.',
          },
          {
            term: 'Put the threshold in the workflow file, not in configuration',
            detail:
              'persistent DebtDrone configuration is per-user, so a fresh CI runner has none of it and would scan with no gate at all. Passing --fail-on explicitly in the pipeline keeps the gate under review like any other change, and makes it impossible to lose by accident.',
          },
        ]}
      />
    </GuideSection>

    <GuideSection title="A pipeline step that does this">
      <GuideParagraph>
        The scan runs against a checkout, so a gate is one step. The command exits non-zero when the
        gate fails, which is all a CI runner needs.
      </GuideParagraph>
      <GuideCode label="GitHub Actions">
        {`- name: Check out the repository
  uses: actions/checkout@v4

- name: Scan for technical debt
  run: debtdrone scan . --fail-on critical --format json`}
      </GuideCode>
      <GuideParagraph>
        Findings go to stdout, analyzer warnings to stderr, and the gate decides the exit code. The
        text format is one row per finding, so a failing build shows you what failed it without
        opening an artifact. <code className="mono rounded bg-surface-2 px-1.5 py-0.5 text-[13px]">--max-complexity</code>{' '}
        defaults to 15, and critical starts above twice that — which is the threshold the message
        below reports:
      </GuideParagraph>
      <GuideCode label="Example output — findings table">
        {`SEVERITY   FILE:LINE                       RULE   MESSAGE
--------   ---------                       ----   -------
CRITICAL   /workspace/src/handler.go:112   N/A    Function 'ProcessRequest' has critical cyclomatic complexity of 32 (threshold: 30)`}
      </GuideCode>
      <GuideParagraph>
        <code className="mono rounded bg-surface-2 px-1.5 py-0.5 text-[13px]">--format json</code>{' '}
        gives you the same findings as data if you would rather publish them as an artifact or feed
        them into another step. Dependency and secret scanning run by default; coverage parsing is
        opt-in and reads existing coverage artifacts rather than running your test suite for you.
      </GuideParagraph>
      <GuideParagraph>
        When the gate trips, the command exits non-zero and says which threshold it hit — the
        message names the severity you configured, so a red build is self-explaining:
      </GuideParagraph>
      <GuideCode label="Example output — gate failure">
        {`quality gate failed: found issues matching or exceeding severity 'critical'`}
      </GuideCode>
    </GuideSection>

    <GuideSection title="The tradeoffs worth deciding deliberately">
      <GuideList
        items={[
          {
            term: 'Blocking versus reporting',
            detail:
              'a blocking gate changes behaviour but costs goodwill when it is wrong. A reporting-only run costs nothing and gets ignored. Most teams want blocking at critical and reporting below it.',
          },
          {
            term: 'Whole repository versus changed files',
            detail:
              'scanning everything gives a stable number that does not depend on the size of the diff, but it means an unrelated pre-existing finding can fail your pull request. Scanning only what changed avoids that and lets old debt sit undisturbed.',
          },
          {
            term: 'Speed versus coverage',
            detail:
              'dependency scanning adds time proportional to the size of your lockfile. If pipeline duration is the binding constraint, run the full scan on a schedule and the gate on pull requests.',
          },
          {
            term: 'A failed scan is not a clean scan',
            detail:
              'if part of a scan fails, treat that as a build problem rather than a pass. DebtDrone reports partial results and the gate outcome together rather than silently returning success.',
          },
        ]}
      />
    </GuideSection>

    <GuideSection title="If you would rather not own the pipeline step">
      <GuideParagraph>
        The hosted product runs the same scanner without a pipeline change. On Pro and Enterprise
        plans, supported pull-request events update one summary comment on the pull request and
        publish a commit status, which you can require in a branch protection rule — the same
        blocking behaviour, configured in GitHub rather than in your workflow file.
      </GuideParagraph>
      <GuideParagraph>
        The tradeoff is where the decision lives. A CLI gate is versioned with your code and works on
        any runner; a commit status is easier to set up and keeps history alongside your{' '}
        <Link to="/technical-debt-scanner" className="text-lime underline underline-offset-4">scan reports</Link>.
        Plan limits are on the{' '}
        <Link to="/pricing" className="text-lime underline underline-offset-4">pricing page</Link>, and
        the <Link to="/blog" className="text-lime underline underline-offset-4">blog</Link> covers how
        the analysis pipeline fits together.
      </GuideParagraph>
    </GuideSection>

    <GuideCta>
      <GuideParagraph>
        Add the gate to a branch and watch one pipeline run before you enforce it anywhere else.
      </GuideParagraph>
      <GuideCtaActions cliHref={`${CLI_DOCS_URL}/headless-usage/`} />
    </GuideCta>
  </GuideLayout>
);

export default CiCdQualityGates;
